import { Flex, Span } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useActiveContactNotes } from '../profile/use-active-contact-notes';
import NoteCard from '../views/NoteCard';
import NoteCardSkeleton from '../views/NoteCardSkeleton';
import NoteComposer from '../views/NoteComposer';
import ContactSection from './ContactSection';

/**
 * NOTES rail section: a collapsible "NOTES" disclosure showing the team's notes
 * for this contact (cards first), with the composer below. Brand-scoped notes
 * are read and added via `useActiveContactNotes` (keyed by the resolved
 * identity); the hook owns the optimistic prepend. A header count mirrors the
 * COLLABORATIONS section's accessory.
 */
const ContactNotesSection: React.FC = () => {
  const { t } = useTranslation();
  const { notes, isLoading, addNote, updateNote, deleteNote } = useActiveContactNotes();

  return (
    <ContactSection
      label={t('contactDrawer.rail.notes')}
      collapsible
      defaultOpen
      accessory={
        notes.length > 0 ? (
          <Span fontFamily="heading" fontSize="11px" fontWeight="semibold" color="primary.400">
            {notes.length}
          </Span>
        ) : undefined
      }
    >
      {isLoading ? (
        <Flex direction="column" gap="8px" mb="10px">
          {Array.from({ length: 2 }).map((_, index) => (
            <NoteCardSkeleton key={index} />
          ))}
        </Flex>
      ) : notes.length > 0 ? (
        <Flex direction="column" gap="8px" mb="10px">
          {notes.map((note) => (
            <NoteCard key={note.id} note={note} onEdit={updateNote} onDelete={deleteNote} />
          ))}
        </Flex>
      ) : (
        <Span display="block" mb="10px" fontFamily="body" fontSize="13px" color="primary.500">
          {t('contactDrawer.notes.empty')}
        </Span>
      )}

      <NoteComposer onAdd={addNote} />
    </ContactSection>
  );
};

export default ContactNotesSection;
